import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useApi, useAction } from '../lib/hooks.js';
import { ErrorNote, Pill, Spinner, announce } from '../components/ui.jsx';
// Every sentence the tour shows. The words live apart from the mechanics so
// rewriting the tour is editing that one file — see the note at its top.
import { TOUR_COPY } from './welcomeCopy.js';

/**
 * The guided tour.
 *
 * Seven steps, one at a time, each pointing at a screen that already exists.
 * Nothing here is a mock-up or a video: every step's button navigates into the
 * real thing, which is why leaving mid-tour is expected rather than an escape.
 *
 * The checkmarks are computed from the same counts the home screen reads, not
 * from "has this person tapped Next". A tour that congratulates you for
 * scrolling teaches nothing; one that can see an empty pantry is telling the
 * truth about where you are.
 *
 * The step index is deliberately not persisted anywhere. Coming back starts at
 * one again, and the checkmarks -- which are real -- carry the progress. A
 * saved cursor would have to be invalidated every time the app changed, and it
 * would put somebody back on a step they had already done.
 */
export default function Welcome() {
  // The home screen's own payload, for the counts. Asking for it here costs one
  // request and means the tour and the dashboard can never disagree.
  const dashboard = useApi('/dashboard');
  const household = useApi('/household/members');

  const [index, setIndex] = useState(0);
  // Two steps have nothing in the database to check against -- reading about
  // how Cook Now ranks is not an act that leaves a trace. Those are ticked by
  // hand, and only for as long as this page is open: writing them down would be
  // recording that somebody read a paragraph, which is not worth a column.
  const [acked, setAcked] = useState({});
  const { busy, error, run } = useAction();

  const counts = dashboard.data?.counts;
  const members = household.data?.members ?? [];
  const countsUnknown = Boolean(dashboard.error);
  const membersUnknown = Boolean(household.error);

  /**
   * Finishing and skipping are the same act as far as the server is concerned:
   * the wizard is over. Recording it before navigating, so a slow write cannot
   * land after the reload and be lost.
   */
  const leave = () => run(async () => {
    await api.post('/household/me/onboarded', {});
    // A FULL navigation, not the router. The app bootstrap reads
    // /household/pending once per load and redirects to this page from "/" when
    // it says the tour is unfinished -- a client-side navigation would arrive
    // at "/" holding the stale answer and bounce straight back here.
    window.location.assign('/');
  });

  // The words come from welcomeCopy.js; everything mechanical — where each
  // button goes, which real count ticks which step — is decided here.
  const STEPS = [
    { key: 'welcome', ...TOUR_COPY.welcome },
    {
      key: 'stock',
      ...TOUR_COPY.stock,
      cta: { to: '/capture', label: TOUR_COPY.stock.cta },
      // Either half counts. A receipt reviewed but not yet filed onto a shelf
      // still means food went in, and telling somebody it did not would be the
      // tour arguing with what they just watched happen.
      source: 'counts',
      done: Boolean(counts && (counts.inventory_items > 0 || counts.master_items > 0)),
    },
    {
      key: 'recipes',
      ...TOUR_COPY.recipes,
      cta: { to: '/recipes/discover', label: TOUR_COPY.recipes.cta },
      source: 'counts',
      done: Boolean(counts && counts.recipes > 0),
    },
    {
      key: 'cook',
      ...TOUR_COPY.cook,
      cta: { to: '/cook-now', label: TOUR_COPY.cook.cta },
      ack: true,
    },
    {
      key: 'shop',
      ...TOUR_COPY.shop,
      cta: { to: '/shopping', label: TOUR_COPY.shop.cta },
      ack: true,
    },
    {
      key: 'share',
      ...TOUR_COPY.share,
      cta: { to: '/settings', label: TOUR_COPY.share.cta },
      // More than one member, rather than "an invite exists": an invitation
      // nobody accepted has not shared anything with anybody.
      source: 'members',
      done: members.length > 1,
    },
    { key: 'done', ...TOUR_COPY.done },
  ];

  // The last step's pointer at the manual, assembled around the copy file's
  // {link} token so the sentence stays editable as plain text.
  const [helpLead, helpTail] = (TOUR_COPY.done.help ?? '{link}').split('{link}');

  const step = STEPS[index];
  const last = index === STEPS.length - 1;
  // A hand-ticked step and a computed one look identical once they are ticked;
  // only one of them is evidence.
  const done = step.ack ? Boolean(acked[step.key]) : Boolean(step.done);

  // The heading changes while focus stays on the Next button, so without this a
  // screen reader user is told nothing happened. Polite, like every other
  // announcement in the app. Skipped on arrival because App already announces
  // the page title, and two messages queued at once is one too many. Guarded on
  // the index value rather than a mount flag: StrictMode runs mount effects
  // twice in development, and a consumed flag would announce step 1 on arrival
  // -- the exact message this exists to suppress.
  const announced = useRef(0);
  useEffect(() => {
    if (announced.current === index) return;
    announced.current = index;
    announce(`Step ${index + 1} of ${STEPS.length}: ${STEPS[index].title}`);
    // On the step only: STEPS is rebuilt every render, and depending on it here
    // would re-announce every time a count came back.
  }, [index]);

  return (
    <div className="stack-lg">
      <div className="stack">
        <div className="row-between">
          <span className="small muted">Step {index + 1} of {STEPS.length}</span>
          {/* On every step, not only the first. Somebody four screens in who has
              decided they would rather poke at it themselves should not have to
              walk to the end to say so. */}
          <button
            type="button"
            className="link small"
            style={{ padding: '10px 0' }}
            disabled={busy}
            onClick={leave}
          >
            Skip the tour
          </button>
        </div>

        {/* Decorative: the step count above says the same thing in words, and
            seven unlabelled dots in the tab order would be seven more stops
            between somebody and the button they want. */}
        <div className="row" aria-hidden="true" style={{ gap: 6 }}>
          {STEPS.map((entry, i) => (
            <span
              key={entry.key}
              style={{
                width: i === index ? 22 : 8,
                height: 8,
                borderRadius: 999,
                background: i === index
                  ? 'var(--accent)'
                  : (entry.ack ? acked[entry.key] : entry.done) ? 'var(--ok)' : 'var(--border)',
              }}
            />
          ))}
        </div>
      </div>

      <ErrorNote error={error} />

      <section className="card stack">
        <div className="row-between">
          <h2>{step.title}</h2>
          {done && <Pill tone="ok">done</Pill>}
        </div>

        <div>
          {step.body.map((line, i) => (
            <p
              key={i}
              className="small muted"
              style={{ marginBottom: i === step.body.length - 1 && step.key !== 'done' ? 0 : '0.65em' }}
            >
              {line}
            </p>
          ))}
          {step.key === 'done' && (
            <p className="small muted" style={{ margin: 0 }}>
              {helpLead}
              <Link to="/help">{TOUR_COPY.done.helpLinkLabel}</Link>
              {helpTail}
            </p>
          )}
        </div>

        {step.cta && (
          <div className="row">
            {/* Straight into the real screen. Coming back to /welcome resumes at
                step one, which is the whole reason the checkmarks exist. */}
            <Link className="button primary grow" to={step.cta.to}>{step.cta.label}</Link>
          </div>
        )}

        {step.ack && (
          <div className="row-between">
            <span className="small muted">
              {done ? 'Marked as read.' : 'Nothing to check against here.'}
            </span>
            <button
              type="button"
              className="small"
              aria-pressed={done}
              onClick={() => setAcked((current) => ({ ...current, [step.key]: !current[step.key] }))}
            >
              {done ? 'Undo' : 'Got it'}
            </button>
          </div>
        )}

        {/* Said on the step it applies to rather than as a banner over the whole
            tour. The read that feeds this step's tick failed, so an unticked
            step here means nothing -- and a tour silently showing every step as
            undone during an outage is the app lying about your own kitchen. */}
        {((step.source === 'counts' && countsUnknown) || (step.source === 'members' && membersUnknown)) && (
          <p className="tiny muted" style={{ margin: 0 }}>
            Could not read your pantry just now, so this step cannot check itself off.
          </p>
        )}
      </section>

      <div className="row" style={{ gap: 10 }}>
        {index > 0 && (
          <button type="button" disabled={busy} onClick={() => setIndex(index - 1)}>Back</button>
        )}
        {last ? (
          <button type="button" className="primary grow" disabled={busy} onClick={leave}>
            {busy ? <><Spinner /> Finishing</> : 'Finish'}
          </button>
        ) : (
          <button type="button" className="primary grow" onClick={() => setIndex(index + 1)}>
            Next
          </button>
        )}
      </div>
    </div>
  );
}
